/**
 * AFF 3D models — draw the aircraft as a mapbox `model` layer (#820).
 * Used in 3D mode in place of the 2D DOM markers.
 */

import type mapboxgl from 'mapbox-gl';
import type { ExpressionSpecification } from 'mapbox-gl';
import { AIRCRAFT_TYPES, affModelUrl, type AircraftStatus } from './affSymbology';
import { buildModelLayer, modelHeightExpression, modelIdFor, modelScaleAt, modelScaleExpression } from './affModelLayer';
import { toModelFeature } from './affModelFeatures';
import { logger } from '../../../utils/logger';

const log = logger('affAircraftModels');

export const MODEL_SOURCE_ID = 'aff-aircraft-models';
export const MODEL_LAYER_ID = 'aff-aircraft-models-layer';

/**
 * Keep the models the same size on screen as the map zooms. Mapbox's own
 * zoom interpolation of model-scale does not follow the camera's zoom
 * (seen 2026-09-11), so the provider sets a literal scale on every zoom —
 * and the lift with it, since the lift grows with the scale.
 */
export function rescaleAircraftModels(map: mapboxgl.Map): void {
  if (!map.getLayer(MODEL_LAYER_ID)) return;
  const scale = modelScaleForZoom(map.getZoom());
  map.setPaintProperty(MODEL_LAYER_ID, 'model-scale', modelScaleExpression(scale) as ExpressionSpecification);
  map.setPaintProperty(MODEL_LAYER_ID, 'model-translation', modelHeightExpression(scale) as ExpressionSpecification);
}

/** What the provider knows about one aircraft, before terrain is applied. */
export interface AircraftForModel {
  /** Who this is, for the warnings when heading or altitude is missing. */
  label: string;
  /** The aircraft's own feature properties, so hover and click behave as on its marker. */
  props: Record<string, unknown>;
  coordinates: [number, number];
  /** Airframe code: RW, BD, TK, CL, FB. */
  code: string;
  status: AircraftStatus;
  heading: number | null | undefined;
  altitudeAslM: number | null | undefined;
}

/**
 * The RW model's largest horizontal extent (glb bounds with node transforms
 * applied). Decided 2026-09-14, seen in the browser: real proportions made the
 * tankers far too big beside the helicopters, so TK, CL and FB are drawn
 * smaller until their largest horizontal extent matches this. BD is left as is.
 */
const RW_EXTENT_M = 11.84;

/**
 * Per-model geometry. `belowOriginM` measured from each glb with node
 * transforms applied (2026-09-11). `offsetDeg` measured in the browser
 * (2026-09-14, #820 defect c): each nose checked from straight above against
 * a known heading and the tail line.
 */
const MODEL_GEOMETRY: Record<string, { belowOriginM: number; offsetDeg: number; sizeFactor: number }> = {
  RW: { belowOriginM: 0.08, offsetDeg: 0, sizeFactor: 1 },
  BD: { belowOriginM: 0.03, offsetDeg: 0, sizeFactor: 1 },
  TK: { belowOriginM: 2.96, offsetDeg: 90, sizeFactor: RW_EXTENT_M / 33.96 },
  CL: { belowOriginM: 4.96, offsetDeg: 90, sizeFactor: RW_EXTENT_M / 27.82 },
  FB: { belowOriginM: 2.59, offsetDeg: 90, sizeFactor: RW_EXTENT_M / 18.06 },
};

/** Fire Boss wingspan; it was sized on screen at ~100 CSS px (2026-09-11). */
const REFERENCE_LENGTH_M = 18.06;
const TARGET_PX = 100;
/** Yellowknife. Per-aircraft latitude correction is deferred. */
const REFERENCE_LATITUDE = 62.46;

/** The shared scale at this zoom; each model is drawn at its size factor of it. */
export function modelScaleForZoom(zoom: number): number {
  return modelScaleAt(REFERENCE_LENGTH_M, TARGET_PX, zoom, REFERENCE_LATITUDE);
}

/**
 * The rendered ground under an aircraft, or null where mapbox has no terrain.
 *
 * Null is normal, not exceptional: mapbox only loads terrain near the view, so
 * any aircraft off screen has none (#820 defect a — C-GKTL, landed in northern
 * Alberta, returned null while every aircraft in view had real ground). The
 * caller decides what an unplaceable aircraft means; this must not throw, or
 * one aircraft stops every other being drawn.
 */
function groundAt(map: mapboxgl.Map, coordinates: [number, number]): number | null {
  return map.queryTerrainElevation(coordinates) ?? null;
}

/** Each map's model files, fetched once and kept as blob URLs. */
const modelBlobUrls = new WeakMap<mapboxgl.Map, Map<string, Promise<string>>>();

/**
 * Register one model from a blob URL of the file fetched with the login cookie.
 *
 * The models are served from the authenticated /aff route, and mapbox's own
 * model loader does not send credentials: on staging every model came back 401
 * and nothing was drawn in 3D (#820, 2026-09-14). Like the app's other backend
 * fetches, this one sends credentials: 'include'. The blob URL is kept, so a
 * basemap change (which drops registered models) re-registers without fetching.
 */
function registerModel(map: mapboxgl.Map, id: string, code: string): void {
  let urls = modelBlobUrls.get(map);
  if (!urls) {
    urls = new Map();
    modelBlobUrls.set(map, urls);
  }
  let blobUrl = urls.get(code);
  if (!blobUrl) {
    const url = affModelUrl(code);
    blobUrl = fetch(url, { credentials: 'include' }).then(async (response) => {
      if (!response.ok) throw new Error(`AFF model ${url} could not be fetched: HTTP ${response.status}`);
      return URL.createObjectURL(await response.blob());
    });
    urls.set(code, blobUrl);
    const cache = urls;
    blobUrl.catch((error) => {
      // Not cached, so the next draw tries again; the failure is never silent.
      cache.delete(code);
      log.error(error);
    });
  }
  blobUrl.then((loaded) => {
    if (!map.hasModel(id)) map.addModel(id, loaded);
  }, () => {});
}

/** Take the model layer and its source off the map (back to 2D). */
export function removeAircraftModels(map: mapboxgl.Map): void {
  if (map.getLayer(MODEL_LAYER_ID)) map.removeLayer(MODEL_LAYER_ID);
  if (map.getSource(MODEL_SOURCE_ID)) map.removeSource(MODEL_SOURCE_ID);
}

/**
 * Draw the aircraft as 3D models.
 *
 * Returns the aircraft that could not be placed for want of terrain under
 * them, so the caller can draw them once terrain loads rather than leaving
 * them undrawn until the next refresh.
 */
export function drawAircraftModels(map: mapboxgl.Map, aircraft: AircraftForModel[]): AircraftForModel[] {
  const skipped: AircraftForModel[] = [];
  // Registered models belong to the style, so a basemap change drops them too;
  // re-register any that are missing on every draw.
  for (const { code } of AIRCRAFT_TYPES) {
    const id = modelIdFor(code);
    if (!map.hasModel(id)) registerModel(map, id, code);
  }

  // No terrain at all happens between a basemap's setStyle and the style.load
  // that puts the terrain back (#866 M6). It is the per-aircraft case writ large:
  // every aircraft is unplaceable for now, not an error. Throwing here threw out
  // of the 3D toggle's map.fire and left the button and the map disagreeing.
  // All are returned as skipped, so the caller's terrain-ready path draws them.
  const terrain = map.getTerrain();
  if (!terrain) {
    log.debug(`AFF 3D models: the map has no terrain yet; ${aircraft.length} aircraft wait for it`);
    return [...aircraft];
  }
  const exaggeration = terrain.exaggeration as number;

  const features = aircraft.flatMap((a) => {
    const geometry = MODEL_GEOMETRY[a.code];
    if (!geometry) throw new Error(`No model geometry for airframe code "${a.code}"`);

    // The height is measured from the terrain under the aircraft, so this is
    // needed for every one of them, not only those without an altitude.
    const ground = groundAt(map, a.coordinates);
    if (ground == null) {
      // Skipped, not placed at a guessed height: a model sat at 0 m or some
      // default would be drawn in the wrong place and look correct. It is
      // off screen, so nothing visible is lost, and it is named so the gap
      // is never silent.
      // Debug, not warn (decided 2026-09-14): it fires on every redraw for every
      // off-screen aircraft, which is noise with a big fleet.
      log.debug(
        `AFF aircraft ${a.label} has no terrain loaded under it at ` +
          `${a.coordinates.join(', ')}; its model is skipped until terrain loads there`,
      );
      skipped.push(a);
      return [];
    }

    // Decided 2026-09-11: drawn without rotation, and the gap made visible.
    if (a.heading == null) log.warn(`AFF aircraft ${a.label} has no heading; drawn without rotation`);
    // Decided 2026-09-11: sat on the terrain surface, and the gap made visible.
    if (a.altitudeAslM == null) log.warn(`AFF aircraft ${a.label} has no altitude; drawn on the terrain surface`);
    const feature = toModelFeature({
      ...a,
      exaggeration,
      groundRenderedM: ground,
      offsetDeg: geometry.offsetDeg,
      belowOriginM: geometry.belowOriginM,
      sizeFactor: geometry.sizeFactor,
    });
    // The aircraft's own properties ride along so hover and click behave as on
    // its marker; the model's own properties win any clash.
    feature.properties = { ...a.props, ...feature.properties };
    return [feature];
  });

  const data: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features };
  const source = map.getSource(MODEL_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
  if (source) {
    source.setData(data);
  } else {
    map.addSource(MODEL_SOURCE_ID, { type: 'geojson', data });
  }

  if (!map.getLayer(MODEL_LAYER_ID)) {
    map.addLayer(
      buildModelLayer({ id: MODEL_LAYER_ID, sourceId: MODEL_SOURCE_ID, scale: modelScaleForZoom(map.getZoom()) }) as mapboxgl.AnyLayer,
    );
  }

  return skipped;
}
